import ApiService from "@/core/services/api.service";

/**
 * billing-review-service.js
 *
 * ============================================================================
 * FRONT-END SEAM for the Agency INSIGHT billing-processing screen.
 * ============================================================================
 *
 * Demo path (ids beginning `YCSS-`) still resolves the local synthetic fixtures
 * below, so the reviewed screen can be demonstrated with no backend. Any other
 * transaction id calls the live read-only server endpoint
 * (`GET /billing/review/:transactionId`) which runs the real assembly pipeline
 * (AR-split -> transaction assembler -> INSIGHT Save:false validation) and
 * returns the identical review model shape.
 *
 * `ledger-` prefixed ids (W3, e.g. `/billing-processing/ledger-123456` from the workbench
 * queue's Review & Post action) carry a K_IbaisTransactions ledger-row id, not a K_Transactions
 * id - the two are not interchangeable (see workbench-service.js SERVER file header). These
 * call `GET /billing/review/by-ledger-row/:ledgerRowId` instead, which resolves the
 * K_Transactions id server-side before running the same review pipeline. A resolution ambiguity
 * degrades to `{ resolutionError }` (200, not an error) - the screen renders it in the existing
 * amber error banner rather than a partial/guessed review.
 *
 * `getBillingReview(transactionId)` returns a Promise of the fully-assembled
 * billing review model that the BillingProcessing screen renders. In this
 * slice (P6-BILLING, front-end only) it resolves synthetic YCSS fixtures.
 *
 * In production this seam WILL BE REPLACED by the real assembly pipeline:
 *
 *     FinRater (price)  ->  AR-split calculator (24% across AR1/AR2/AR3)
 *                       ->  transaction assembler
 *                       ->  INSIGHT `Save:false` dry-run (validity + errors)
 *
 * ...producing the identical model shape. NOTHING in this file talks to a
 * backend, INSIGHT, or Zoho - it is pure client-side mock data. Do NOT add
 * network calls here; when the real pipeline lands it replaces this module
 * behind the same `getBillingReview()` contract.
 *
 * Financial figures are the synthetic "YCSS" reconciliation set only - no PII.
 * Authoritative given figures: base 30,372.00; PB 10% = 3,037.20;
 * Fuse 11.75% = 3,568.71 (+ broker fee 1,280.00); GC 2.25% = 683.37;
 * total commission 24% = 7,289.28; Gross 37,824.03; Net to insurer 28,397.82.
 * (GST at 10% and the government-charge breakdown are derived so the whole
 * reconciles to the cent - see NOTES at the foot of this file.)
 *
 * Model shape returned by getBillingReview():
 * {
 *   transactionId, transactionType,
 *   policy:   { number, client, insurer, period },
 *   premium:  { base, fsl, stampDuty, insurerGst, gross },
 *   arSplit:  [ { role, repId, pct, commission, gst, fee, feeGst } ]  // FIXED order PB, Fuse, GC
 *   money:    { clientReceivable, insurerPayable, totalIncome },
 *   trusts:   {
 *     insightValid:   { pass, detail },
 *     reconciled:     { pass, detail },
 *     splitIntegrity: { pass, detail }
 *   },
 *   status: 'unposted' | 'posted',
 *   validationErrors: [ ... ],
 *   lastValidatedAt: ISO string | null
 * }
 */

/**
 * The clean, all-trusts-pass YCSS fixture. Post is armable.
 * @type {Object}
 */
export const billingReviewFixture = {
  transactionId: "YCSS-NB-0001",
  transactionType: "New Business",
  policy: {
    number: "MSI-FLEET-000001",
    client: "YCSS (sample)",
    insurer: "MS Amlin (MSI)",
    period: "01/07/2026 - 30/06/2027"
  },
  premium: {
    base: 30372.0,
    fsl: 0, // authoritative (YCSS - no FSL on this risk)
    stampDuty: 3006.83, // authoritative (YCSS)
    insurerGst: 3037.2, // authoritative (YCSS)
    gross: 36416.03 // authoritative gross PREMIUM (base + taxes); invoice total incl. fee = 37,824.03
  },
  // FIXED order - AR1 Producing Broker -> AR2 Fuse Fleet -> AR3 Guy Carpenter.
  // Invoice templates depend on this order; never re-sort.
  arSplit: [
    {
      role: "Producing Broker",
      repId: "AR1",
      pct: 10.0,
      commission: 3037.2,
      gst: 303.72,
      fee: 0,
      feeGst: 0
    },
    {
      role: "Fuse Fleet",
      repId: "AR2",
      pct: 11.75,
      commission: 3568.71,
      gst: 356.87,
      fee: 1280.0, // Broker Fee sits on AR2
      feeGst: 128.0
    },
    {
      role: "Guy Carpenter",
      repId: "AR3",
      pct: 2.25,
      commission: 683.37,
      gst: 68.34,
      fee: 0,
      feeGst: 0
    }
  ],
  money: {
    // Client pays the gross premium; broker fee + GST are funded within it.
    clientReceivable: 37824.03,
    // Net to insurer (authoritative YCSS figure).
    insurerPayable: 28397.82,
    // Retained by the DKG AR network = commission + comm GST + fee + fee GST
    // = sum of the three AR payables. Ties out: income + insurer = client.
    totalIncome: 9426.21
  },
  trusts: {
    insightValid: {
      pass: true,
      detail:
        "INSIGHT Save:false dry-run returned IsValid:true - commission (24%) within base-premium cap, roll-ups accepted."
    },
    reconciled: {
      pass: true,
      detail:
        "Gross $37,824.03 and net to insurer $28,397.82 tie to the source figures to the cent."
    },
    splitIntegrity: {
      pass: true,
      detail:
        "24% distributed AR1 10% / AR2 11.75% (+fee) / AR3 2.25% in fixed order; AR payables + insurer payable reconcile to client receivable."
    }
  },
  status: "unposted",
  validationErrors: [],
  lastValidatedAt: "2026-07-02T03:12:00.000Z"
};

/**
 * Exception fixture: a reconciliation break (T2 fails). Screen-computed
 * cent-level variance that INSIGHT does NOT catch on its own. Post is disarmed.
 * @type {Object}
 */
export const billingReviewExceptionFixture = {
  transactionId: "YCSS-NB-EX01",
  transactionType: "New Business",
  policy: {
    number: "MSI-FLEET-000042",
    client: "YCSS (sample)",
    insurer: "MS Amlin (MSI)",
    period: "01/07/2026 - 30/06/2027"
  },
  premium: {
    base: 30372.0,
    fsl: 0,
    stampDuty: 3006.83,
    insurerGst: 3037.2,
    gross: 36416.03
  },
  arSplit: [
    {
      role: "Producing Broker",
      repId: "AR1",
      pct: 10.0,
      commission: 3037.2,
      gst: 303.72,
      fee: 0,
      feeGst: 0
    },
    {
      role: "Fuse Fleet",
      repId: "AR2",
      pct: 11.75,
      commission: 3568.71,
      gst: 356.87,
      fee: 1280.0,
      feeGst: 128.0
    },
    {
      role: "Guy Carpenter",
      repId: "AR3",
      pct: 2.25,
      commission: 683.37,
      gst: 68.34,
      fee: 0,
      feeGst: 0
    }
  ],
  money: {
    clientReceivable: 37824.03,
    insurerPayable: 28397.82,
    totalIncome: 9426.21
  },
  trusts: {
    insightValid: {
      pass: true,
      detail:
        "INSIGHT Save:false dry-run returned IsValid:true - INSIGHT's internal rules pass."
    },
    reconciled: {
      pass: false,
      detail:
        "Reconciliation break: source Gross is $37,824.03 but INSIGHT returned $37,824.13 - a $0.10 variance. INSIGHT does not flag this; the screen does."
    },
    splitIntegrity: {
      pass: true,
      detail:
        "24% distributed AR1 10% / AR2 11.75% (+fee) / AR3 2.25% in fixed order; split ties out."
    }
  },
  status: "unposted",
  validationErrors: [
    "Gross premium mismatch: source $37,824.03 vs INSIGHT $37,824.13 (variance $0.10). Return to the pricing step to correct government charges before posting."
  ],
  lastValidatedAt: "2026-07-02T03:20:00.000Z"
};

/**
 * Resolve the billing review model for a transaction.
 *
 * DEMO SEAM: transaction ids beginning `YCSS-` resolve a synthetic fixture (ids
 * containing "EX" resolve the exception fixture, so the failing-trust state can
 * be previewed at e.g. /billing-processing/YCSS-NB-EX01). Any other id is
 * resolved live from the server.
 *
 * @param {string} transactionId
 * @returns {Promise<Object>} the billing review model (shape documented above)
 */
export function getBillingReview(transactionId) {
  const isDemo =
    typeof transactionId === "string" && /^YCSS-/.test(transactionId);

  if (!isDemo) {
    const isLedgerRow =
      typeof transactionId === "string" && /^ledger-/.test(transactionId);

    if (isLedgerRow) {
      // W3: the route carries a K_IbaisTransactions ledger-row id, not a K_Transactions id -
      // resolve it server-side before running the review pipeline.
      const ledgerRowId = transactionId.replace(/^ledger-/, "");
      return ApiService.get(`/billing/review/by-ledger-row/${ledgerRowId}`).then(
        ({ data }) => data.result
      );
    }

    // Live path: the server runs the real read-only assembly pipeline and
    // returns the identical review model shape via the SUCCESS envelope.
    return ApiService.get(`/billing/review/${transactionId}`).then(
      ({ data }) => data.result
    );
  }

  const isException = transactionId.toUpperCase().includes("EX");

  const source = isException
    ? billingReviewExceptionFixture
    : billingReviewFixture;

  // Deep clone so callers can never mutate the shared fixture, and stamp the
  // requested id onto the returned model.
  const model = JSON.parse(JSON.stringify(source));
  if (transactionId) {
    model.transactionId = transactionId;
  }

  return Promise.resolve(model);
}

/*
 * NOTES on the premium breakdown (authoritative YCSS figures - live INSIGHT-validated)
 * ------------------------------------------------------------------------------------
 *   base        = 30,372.00
 *   fsl         =      0.00   (no FSL on this risk)
 *   stampDuty   =  3,006.83
 *   insurerGst  =  3,037.20
 *   gross PREMIUM = 36,416.03 (= base + fsl + stampDuty + insurerGst, exact)
 * The INVOICE TOTAL (client receivable) additionally carries the broker fee:
 *   36,416.03 + 1,280.00 (fee) + 128.00 (fee GST) = 37,824.03
 *
 * Money-flow tie-out (split-integrity trust)
 * ------------------------------------------
 * AR payable = commission + gst (+ fee + feeGst on AR2):
 *   AR1 = 3,037.20 + 303.72                       = 3,340.92
 *   AR2 = 3,568.71 + 356.87 + 1,280.00 + 128.00   = 5,333.58
 *   AR3 =   683.37 +  68.34                        =   751.71
 *   sum AR payables (= totalIncome)                = 9,426.21
 * clientReceivable = totalIncome + insurerPayable
 *                  = 9,426.21 + 28,397.82 = 37,824.03  (= invoice total, exact)
 */
